import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

type Industry = components["schemas"]["IndustryResponse"];
type Group = components["schemas"]["MerchantCategoryGroup"];

const groupLabels: Record<NonNullable<Group>, string> = {
  Agriculture: "Agriculture",
  ContractedServices: "Contracted Services",
  Transportation: "Transportation",
  Utilities: "Utilities",
  RetailStores: "Retail Stores",
  Clothing: "Clothing",
  MiscellaneousRetail: "Miscellaneous Retail",
  FinancialServices: "Financial Services",
  PersonalServices: "Personal Services",
  BusinessServices: "Business Services",
  ProfessionalServices: "Professional Services",
  Government: "Government",
  Entertainment: "Entertainment",
  Travel: "Travel",
};

function groupLabelFor(group: Group | undefined): string {
  if (!group) return "Other";
  return groupLabels[group] ?? group;
}

export function IndustriesTable() {
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);

  useEffect(() => {
    let cancelled = false;
    async function fetchIndustries() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/v1/industries");
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load industries.");
      } else {
        setIndustries(data ?? []);
      }
      setLoading(false);
    }
    fetchIndustries();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    if (!query) return industries;
    return industries.filter((industry) => {
      const groupName = groupLabelFor(industry.group).toLowerCase();
      return (
        industry.code.toLowerCase().includes(query) ||
        industry.displayName.toLowerCase().includes(query) ||
        industry.slug.toLowerCase().includes(query) ||
        groupName.includes(query)
      );
    });
  }, [industries, debouncedSearch]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <Input
          placeholder="Search industries..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
        {loading && <Spinner className="size-5" />}
        {!loading && (
          <span className="text-sm text-muted-foreground">
            {filtered.length}{" "}
            {filtered.length === 1 ? "industry" : "industries"}
          </span>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Group</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && !loading ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-muted-foreground h-24 text-center"
                >
                  No industries found.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((industry) => (
                <TableRow key={industry.id ?? industry.code}>
                  <TableCell>
                    <Badge variant="secondary" className="font-mono text-xs">
                      {industry.code}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">
                    {industry.displayName}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {industry.slug}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {groupLabelFor(industry.group)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
